import React from 'react';
import { FormattedMessage } from 'react-intl';

import Time from 'src/enosikit/components/Time/components/Time';
import {
  PLATFORM_MODE_REBATE, PLATFORM_MODE_TRADE, SYSTEM_USER_ID, TRADE_DIRECTION_BUY,
  TRADE_DIRECTION_SELL, TRADE_DIRECTION_UNSPECIFIED,
} from 'src/util/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { getStringAndNumericFormattedDate, getTradeDirection } from 'src/util/i18n/helpers';

import { valuePerUnitWithStrong, valueRangePerUnitWithStrong } from './energyPrice';

/**
 * Generates internationalized price information display for trade rules, handling different
 * platform modes (trade vs rebate), pricing structures (flat vs time-of-use), and display
 * contexts (timeline vs standard view).
 *
 * Used throughout the application to consistently format trade rule pricing information
 * in trade rule lists, timelines, active trade displays, and customer proposals.
 * @param {object} params - Function parameters
 * @param {import('react-intl').IntlShape} params.intl - React Intl internationalization object
 *   for formatting messages
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} params.direction - Trade
 *   direction from GraphQL schema (TRADE_DIRECTION_BUY or TRADE_DIRECTION_SELL)
 * @param {number} params.maximum - Maximum price value
 * @param {number} params.minimum - Minimum price value
 * @param {PLATFORM_MODE_REBATE|PLATFORM_MODE_TRADE} params.mode - Platform operating
 *   mode determining price display format (rebate shows discounts, trade shows prices)
 * @param {React.ReactElement} [params.detailsLink=null] - Optional React element containing
 *   link to trade rule details page
 * @returns {React.ReactElement|React.ReactNode[]|string|null} Returns FormattedMessage JSX element
 * for standardview with detailsLink support, formatted string with JSX interpolation for timeline
 *  view, or null if no matching conditions
 * @example
 * // Standard trade mode with flat pricing
 * buildTradeRulePriceInformation({
 *   intl,
 *   direction: TRADE_DIRECTION_BUY,
 *   minimum: 0.15,
 *   maximum: 0.15,
 *   mode: PLATFORM_MODE_TRADE,
 *   detailsLink: <Link to="/trade-rules/123">details</Link>
 * });
 * // Returns: <FormattedMessage> "Buy price of 15.000 c/kWh (details)"
 * @example
 * // Rebate mode with time-of-use pricing for timeline
 * buildTradeRulePriceInformation({
 *   intl,
 *   direction: TRADE_DIRECTION_SELL,
 *   minimum: 0.10,
 *   maximum: 0.25,
 *   mode: PLATFORM_MODE_REBATE,
 * });
 * // Returns: "Selling at a discount of 10.000 to 25.000 c/kWh"
 */
export const buildTradeRulePriceInformation = ({
  intl, direction, maximum, minimum, mode, detailsLink = null,
}) => {
  const intlCurrency = useIntlCurrency();
  const priceAndUnit = valuePerUnitWithStrong(intl, intlCurrency, minimum);
  const priceRange = valueRangePerUnitWithStrong(intl, intlCurrency, { minimum, maximum });
  const tradeDirection = getTradeDirection(intl, direction);

  if (mode === PLATFORM_MODE_REBATE && minimum === maximum) {
    return (
      <FormattedMessage
        id="trade_rule.i18n.helpers.price_information.flat_price.rebate_mode.text"
        defaultMessage="Discount of {priceAndUnit} {detailsLinkFormatted}"
        values={{
          priceAndUnit,
          detailsLinkFormatted: detailsLink ? (
            <>
              (
              {detailsLink}
              )
            </>
          ) : null,
        }}
      />
    );
  }

  if (mode !== PLATFORM_MODE_REBATE && minimum === maximum) {
    return (
      <FormattedMessage
        id="trade_rule.i18n.helpers.price_information.flat_price.trade_mode.text"
        defaultMessage="{tradeDirection} price of {priceAndUnit} {detailsLinkFormatted}"
        values={{
          tradeDirection,
          priceAndUnit,
          detailsLinkFormatted: detailsLink ? (
            <>
              (
              {detailsLink}
              )
            </>
          ) : null,
        }}
      />
    );
  }

  if (mode === PLATFORM_MODE_REBATE && minimum !== maximum) {
    return (
      <FormattedMessage
        id="trade_rule.i18n.helpers.price_information.time_of_use_price.rebate_mode.text"
        defaultMessage="Discount of {priceRange} {detailsLinkFormatted}"
        values={{
          priceRange,
          detailsLinkFormatted: detailsLink ? (
            <>
              (
              {detailsLink}
              )
            </>
          ) : null,
        }}
      />
    );
  }

  if (mode !== PLATFORM_MODE_REBATE && minimum !== maximum) {
    return (
      <FormattedMessage
        id="trade_rule.i18n.helpers.price_information.time_of_use_price.trade_mode.text"
        defaultMessage="{tradeDirection} price of {priceRange}{detailsLinkFormatted}"
        values={{
          tradeDirection,
          priceRange,
          detailsLinkFormatted: detailsLink ? (
            <>
              {' '}
              (
              {detailsLink}
              )
            </>
          ) : null,
        }}
      />
    );
  }

  return null;
};

/**
 * Retrieves the appropriate trade point label based on the given trade direction.
 * @param {import('react-intl').IntlShape} intl - i18n react-intl.
 * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL
 * | TRADE_DIRECTION_UNSPECIFIED | string} direction - The trade direction.
 * @returns {string} The appropriate trade point label.
 */
export const getTradePointLabel = (intl, direction) => {
  let tradePointLabel;

  const tradePointLabelBuy = intl.formatMessage({ id: 'trade_rule.i18n.helpers.trade_point_label.buyer.text', defaultMessage: 'Choose the Trade Point you are buying for' });
  const tradePointLabelSell = intl.formatMessage({ id: 'trade_rule.i18n.helpers.trade_point_label.seller.text', defaultMessage: 'Choose the Trade Point you are selling from' });
  const tradePointLabelGeneral = intl.formatMessage({ id: 'trade_rule.i18n.helpers.trade_point_label.general.text', defaultMessage: 'Choose the Trade Point' });

  switch (direction) {
    case TRADE_DIRECTION_BUY:
      tradePointLabel = tradePointLabelBuy;
      break;
    case TRADE_DIRECTION_SELL:
      tradePointLabel = tradePointLabelSell;
      break;
    default:
      tradePointLabel = tradePointLabelGeneral;
      break;
  }
  return tradePointLabel;
};

/**
 * Builds an internationalized "action [by actor] on timestamp" message, conditionally
 * omitting the actor clause when the actor cannot be identified.
 *
 * The actor is considered known when a resolved user object is provided, or when the raw
 * actor id matches the System user. When the actor is known, descriptor is used and
 * the {actor} placeholder is populated via displayUserName. Otherwise fallbackDescriptor is
 * used, dropping the "by {actor}" portion entirely (e.g. for deleted or deactivated accounts).
 * @param {import('react-intl').IntlShape} intl - React Intl internationalization object.
 * @param {object} options - Actor, timestamp, and message descriptors.
 * @param {object|null} options.user - Resolved user object (User type from
 *   graphql/syndesi.graphql).
 * @param {string|null} options.userId - Raw actor id recorded on the trade rule.
 * @param {number|null} options.timestamp - Unix timestamp of the action.
 * @param {{id: string, defaultMessage: string}} options.descriptor - Descriptor for the
 *   message including the actor, e.g. "Proposed by {actor} on {timestamp}".
 * @param {{id: string, defaultMessage: string}} options.fallbackDescriptor - Descriptor for the
 *   message excluding the actor, e.g. "Proposed on {timestamp}".
 * @returns {React.ReactNode[]|string} The formatted message.
 */
export const buildActorTimestampMessage = (
  intl,
  {
    user,
    userId,
    timestamp,
    descriptor,
    fallbackDescriptor,
  },
) => {
  const timestampNode = (
    <Time
      child={getStringAndNumericFormattedDate(intl, timestamp)}
      unixTimestamp={timestamp}
    />
  );

  const actorKnown = !!(user || userId === SYSTEM_USER_ID);

  if (actorKnown) {
    return intl.formatMessage(descriptor, {
      actor: <strong>{displayUserName(intl, user, userId)}</strong>,
      timestamp: timestampNode,
    });
  }

  return intl.formatMessage(fallbackDescriptor, { timestamp: timestampNode });
};
